"use client";

import { useEffect, useState } from "react";
import styles from "./TickerBar.module.css";

const MAX_TICKER_ITEMS = 24;

export interface TickerItem {
  symbol: string;
  close: number;
  change: number;
  change_pct: number;
}

export interface TickerSnapshot {
  items: TickerItem[];
  sessionDate: string | null;
}

function formatPrice(value: number) {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatChange(value: number) {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

function formatSessionDate(value: string | null) {
  if (!value) return "Last session";
  const asDate = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(asDate.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(asDate);
}

async function fetchTickerSnapshot(): Promise<TickerSnapshot> {
  try {
    const response = await fetch("/api/prices/ticker", {
      cache: "no-store",
    });
    if (!response.ok) {
      return { items: [], sessionDate: null };
    }

    const data = await response.json();
    const items = Array.isArray(data.results) ? data.results : [];
    const sessionDate = typeof data.session_date === "string" ? data.session_date : null;
    return { items, sessionDate };
  } catch {
    return { items: [], sessionDate: null };
  }
}

export function TickerBarView({
  items,
  sessionDate,
  loading = false,
}: TickerSnapshot & { loading?: boolean }) {
  const tickerItems = items.slice(0, MAX_TICKER_ITEMS);
  const displayItems = tickerItems.length > 0 ? [...tickerItems, ...tickerItems] : [];
  const sessionLabel = formatSessionDate(sessionDate);

  if (loading) {
    return (
      <div className={styles.tickerBar} aria-label="Loading last session PSX prices">
        <div className={styles.tickerShell}>
          <div className={styles.tickerMeta}>
            <span className={styles.tickerLabel}>Last Session Prices</span>
            <span className={styles.tickerDate} aria-hidden="true">
              Last session
            </span>
          </div>
          <div className={styles.tickerEmpty}>Loading last session PSX prices...</div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.tickerBar} aria-label="Last session PSX prices">
        <div className={styles.tickerShell}>
          <div className={styles.tickerMeta}>
            <span className={styles.tickerLabel}>Last Session Prices</span>
            <span className={styles.tickerDate} aria-hidden="true">
              Last session
            </span>
          </div>
          <div className={styles.tickerEmpty}>Last session PSX prices unavailable.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tickerBar} aria-label={`Last session PSX prices for ${sessionLabel}`}>
      <div className={styles.tickerShell}>
        <div className={styles.tickerMeta}>
          <span className={styles.tickerLabel}>Last Session Prices</span>
          <span className={styles.tickerDate}>{sessionLabel}</span>
        </div>
        <div className={styles.tickerViewport}>
          <div className={styles.tickerTrack}>
            {displayItems.map((item, index) => {
              const toneClass =
                item.change_pct > 0 ? "up" : item.change_pct < 0 ? "down" : "flat";

              return (
                <div key={`${item.symbol}-${index}`} className={`${styles.tickerItem} tabular-nums`}>
                  <span className={styles.tickerSymbol}>{item.symbol}</span>
                  <span className={styles.tickerPrice}>{formatPrice(item.close)}</span>
                  <span className={`${styles.tickerChange} ${toneClass}`}>{formatChange(item.change_pct)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TickerBar() {
  const [snapshot, setSnapshot] = useState<TickerSnapshot>({ items: [], sessionDate: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const nextSnapshot = await fetchTickerSnapshot();
      if (!cancelled) {
        setSnapshot(nextSnapshot);
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  return <TickerBarView {...snapshot} loading={loading} />;
}
